import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Multistep } from '@/components/multistep/Multistep';
import { Step } from '@/components/multistep/Questionnaire';
import { Address } from '@/services/api/commonTypes';
import { ContractStatus } from '@/services/api/contracts/partners';
import { useCompany, useCreateCompanyNda } from '@/services/api/hooks/companies';
import { useCompanyId, usePartnerId } from '@/services/authentication/hooks/user';
import { CSVProvider } from '@/services/csv/CSVService';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, Suspense, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import BusinessStep from '../commonSteps/BusinessStep';
import SignTermsAndConditionsStep, { SignerInfo } from '../commonSteps/SignTermsAndConditionsStep';
import { deserialiseAddress, serialiseAddress } from '../commonSteps/addressHelper';
import CompanyOnboardingQuestionnaireTAndCControls from './CompanyOnboardingQuestionaireTermsAndConditonsControls';

interface CompanyOnboardingQuestionnaireProps {}

const CompanyOnboardingQuestionnaireFields = z.object({
  companyInfo: z.object({
    name: z.string(),
    number: z.string(),
    address: Address,
    employeeCount: z.number()
  }),
  signerInfo: SignerInfo
});

export type CompanyOnboardingQuestionnaireFields = z.infer<typeof CompanyOnboardingQuestionnaireFields>;

export const CompanyOnboardingQuestionnaire: FC<CompanyOnboardingQuestionnaireProps> = () => {
  const partnerId = usePartnerId();
  const companyId = useCompanyId();

  if (!partnerId || !companyId) {
    throw Error('Partner ID and Company ID must be provided');
  }

  const [company] = useCompany(partnerId, companyId);

  const createNda = useCreateCompanyNda(partnerId, companyId);

  const navigate = useNavigate();

  useEffect(() => {
    if (company.contractStatus !== ContractStatus.Pending && company.contractStatus !== ContractStatus.Applied) {
      navigate('/');
    }
  }, []);

  const methods = useForm<CompanyOnboardingQuestionnaireFields>({
    resolver: zodResolver(CompanyOnboardingQuestionnaireFields),
    defaultValues: {
      companyInfo: {
        name: company.registeredInfo.name,
        number: company.registeredInfo.number,
        address: deserialiseAddress(company.registeredInfo.address),
        employeeCount: company.registeredInfo.employeeCount
      }
    },
    resetOptions: {
      keepErrors: true
    }
  });

  const onSubmit = useCallback(async () => {
    const data = methods.getValues();

    const result = await createNda.mutateAsync({
      regNumber: data.companyInfo.number,
      regAddress: serialiseAddress(data.companyInfo.address),
      signerEmail: data.signerInfo.email,
      signerName: data.signerInfo.name,
      signerPosition: data.signerInfo.position
    });

    if (result.viewUrl) {
      window.location.replace(result.viewUrl);
    }
  }, []);

  const errorObj = createNda.error as Error | undefined;
  const error = errorObj ? errorObj.message : '';

  return (
    <div className="space-y-3">
      <Form methods={methods} onSubmit={() => {}}>
        <CSVProvider>
          <Multistep onFinish={onSubmit}>
            <Suspense fallback={<Loading />}>
              <Step
                validate={async () => await methods.trigger('companyInfo')}
                key="business-details"
                id="business-details"
                title={'Tell us about your business'}
              >
                <BusinessStep readonly />
              </Step>
              <Step
                key="sign-terms"
                validate={async () => await methods.trigger('signerInfo')}
                id="sign-terms"
                title={'Sign terms & conditions'}
                controls="manual"
              >
                <SignTermsAndConditionsStep />
                {error && <p className="text-red-500 mt-2">{error}</p>}
                <CompanyOnboardingQuestionnaireTAndCControls />
              </Step>
            </Suspense>
          </Multistep>
        </CSVProvider>
      </Form>
    </div>
  );
};
